/// <amd-module name="Wit/WorkItemForm/History/HistoryTab" />
import { IVssPageContext } from "VSS/Platform/Context";
import { useIsMounted, useService } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ZeroData } from "VSSUI/ZeroData";
import * as Resources from "Wit/WorkItemForm/Resources";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { CollapsibleSection } from "../Common/Components";
import { useFieldMetadata, useWorkItem } from "../Common/Hooks";
import { IHistoryItem, IWorkItemFormService } from "../Common/Services";
import { isStateUpdate } from "./HistoryUtils";
import { StateGraph } from "./StateGraph";
import { hasChanges } from "./UpdateSummary";
import { UpdatesList } from "./UpdatesList";

export interface IHistoryTabProps {
    pageContext: IVssPageContext;
}

export function HistoryTab({ pageContext }: IHistoryTabProps) {
    const formService = useService<IWorkItemFormService>("IWorkItemFormService");
    const workItem = useWorkItem();
    const fieldLookup = useFieldMetadata();

    const [updates, setUpdates] = useState<IHistoryItem[] | undefined>();

    const stateUpdates = useMemo(() => (updates ? updates.filter(isStateUpdate).reverse() : []), [updates]);
    const isMounted = useIsMounted();

    // Initialize attachments tab data
    useEffect(() => {
        formService.getHistoryData(workItem.id, workItem.project.guid).then(updates => {
            const updateState = () => setUpdates(updates.filter(update => hasChanges(update, fieldLookup, workItem)));
            if (isMounted()) {
                workItem.refreshNewLinks({ includeHistory: true, keepDeleted: true }).then(updateState).catch(updateState);
            }
        });
    }, []);

    if (!updates) {
        return <Spinner className="flex-grow flex-center justify-center" size={SpinnerSize.medium} />;
    }

    if (updates.length === 0) {
        return (
            <CollapsibleSection label={Resources.History}>
                <ZeroData
                    iconProps={{ iconName: "History" }}
                    imageAltText={Resources.HistoryZeroStateText}
                    secondaryText={Resources.HistoryZeroStateText}
                />
            </CollapsibleSection>
        );
    }

    return (
        <div className="work-item-form-tab full-width">
            <CollapsibleSection label={Resources.StateGraph}>
                <StateGraph stateUpdates={stateUpdates} />
            </CollapsibleSection>

            <CollapsibleSection label={Resources.History}>
                <UpdatesList updates={updates} pageContext={pageContext} />
            </CollapsibleSection>
        </div>
    );
}
